import { useTranslation } from 'react-i18next';
import { ITopic, ITopicGroup, ITopicTag, TNullable, TopicStatus } from '../../common/types';
import { TOPICS_DROPDOWN_SELECTOR_MIN } from '../../constants/app';
import { cn } from '../../lib/utils';
import StaticAutocompleteInput from '../common/static-autocomplete-input';
import { Button } from '../ui/button';

interface TopicSelectorProps {
  topics: ITopic[];
  topicGroup: TNullable<ITopicGroup>;
  topicTag?: TNullable<ITopicTag>;
  selectTopic: (topicId: string) => void;
  onCancel?: () => void;
}

function TopicSelector(props: TopicSelectorProps) {
  const { t } = useTranslation();
  const activeTopics = props.topics.filter((topic) => topic.status === TopicStatus.Active);
  const availableTopics = getAvailableTopics(
    activeTopics,
    props.topicGroup,
    props.topicTag ?? null
  );

  return (
    <div className="mt-6 text-sm rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_10px_25px_rgba(15,23,42,0.06)] animate-enter">
      <p className="mb-3 text-sm text-slate-600">
        {props.topicGroup || props.topicTag
          ? t('chat.topic_select.not_found_topic_group')
          : props.onCancel
            ? t('chat.topic_select.manual')
            : t('chat.topic_select.not_found')}
      </p>
      {availableTopics.length > TOPICS_DROPDOWN_SELECTOR_MIN ? (
        <div className="w-full sm:min-h-72 min-h-80">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <StaticAutocompleteInput
                suggestions={availableTopics.map((topic) => topic.displayName)}
                placeholder={t('common.search_topics')}
                onSelect={(v) => {
                  const topic = availableTopics.find((topic) => topic.displayName === v);
                  if (topic) {
                    props.selectTopic(topic.id);
                  }
                }}
                id="topic-selector"
                autoComplete="off"
                autoFocus
              />
            </div>
            {props.onCancel && (
              <Button variant="outline" onClick={props.onCancel}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {props.onCancel && (
            <div className="mb-3 flex justify-end">
              <Button variant="outline" onClick={props.onCancel}>
                {t('common.cancel')}
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {availableTopics.map((topic) => (
              <Button
                key={topic.id}
                className={cn(
                  'uppercase bg-[hsl(var(--xircle-secondary))] text-slate-900 hover:shadow-sm'
                )}
                onClick={() => props.selectTopic(topic.id)}
              >
                {topic.displayName || topic.name}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function getAvailableTopics(
  activeTopics: ITopic[],
  topicGroup: TNullable<ITopicGroup>,
  topicTag: TNullable<ITopicTag>
) {
  if (topicTag) {
    return activeTopics.filter((topic) => topic.tags.some((tag) => tag.id === topicTag.id));
  }

  if (topicGroup) {
    return activeTopics.filter((topic) => topic.groupId === topicGroup.id);
  }

  return activeTopics;
}

export default TopicSelector;
